import { type ReactNode, useEffect, useRef, useState } from 'react';

type BackgroundVideoProps = {
  name: string;
  className: string;
  tone?: 'green' | 'beige';
  active?: boolean;
  eager?: boolean;
  motionId?: string;
  children?: ReactNode;
};

/** Defer film decoding until the background can actually be seen. */
export function BackgroundVideo({
  name,
  className,
  tone = 'beige',
  active = true,
  eager = false,
  motionId,
  children,
}: BackgroundVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);
  const poster = `/assets/videos/${name}-poster-00001.jpg`;

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    const update = () => {
      const shouldPlay = active && visible && !preference.matches && !document.hidden;
      if (shouldPlay) {
        setLoaded(true);
        void video.play().catch(() => {
          /* The poster remains visible if autoplay is unavailable. */
        });
      } else video.pause();
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting && video.getBoundingClientRect().width > 0;
        update();
      },
      { rootMargin: eager ? '0px' : '100px' },
    );
    observer.observe(video);
    video.addEventListener('loadeddata', update);
    document.addEventListener('visibilitychange', update);
    preference.addEventListener('change', update);
    return () => {
      observer.disconnect();
      video.pause();
      video.removeEventListener('loadeddata', update);
      document.removeEventListener('visibilitychange', update);
      preference.removeEventListener('change', update);
    };
  }, [active, eager]);

  return (
    <div
      data-motion-id={motionId}
      className={`${className} w-background-video`}
      style={{ backgroundImage: `url("${poster}")` }}
    >
      <video
        ref={ref}
        muted
        loop
        playsInline
        poster={poster}
        preload="none"
        tabIndex={-1}
        aria-hidden="true"
      >
        {loaded && (
          <>
            <source src={`/assets/videos/${name}-transcode.mp4`} type="video/mp4" />
            <source src={`/assets/videos/${name}-transcode.webm`} type="video/webm" />
          </>
        )}
      </video>
      {children}
      <div className={`video_color is-${tone}`} />
      <div className={`video_filter is-${tone}`} />
    </div>
  );
}
